/**
 * ManageAssociationsModal Component
 * 
 * A modal component for managing document associations after upload.
 * Reuses the hierarchical association interface from UploadModal but focused
 * on editing existing document connections.
 * 
 * Features:
 * - View current document associations
 * - Add new associations
 * - Remove existing associations
 * - Relationship-aware suggestions
 * - Validation and error handling
 * 
 * @component
 */

import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';
import { useAuth, useTrip } from '@trip-planner-monorepo/shared';
import { daysService } from '@trip-planner-monorepo/days';
import { accommodationService } from '@trip-planner-monorepo/accommodation';
import { placesService } from '@trip-planner-monorepo/places';
import { travelService } from '@trip-planner-monorepo/travel';
import { documentService } from '../services/documentService';
import { linkPresentationService } from '@trip-planner-monorepo/shared/services/linkPresentationService';
import { linkRegistryConfigService } from '@trip-planner-monorepo/shared/services/linkRegistryConfigService';
import { shouldReadFromRegistry } from '@trip-planner-monorepo/shared/utils/linkRegistryModeUtils';
import { toRegistryEntityType, associationEntityTypesMatch } from '../utils/associationEntityTypes';
import { FaChevronDown, FaChevronRight, FaCalendarAlt, FaHotel, FaPlane, FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import './UploadModal.css'; // Reuse existing styles

function ManageAssociationsModal({ document, onClose, onAssociationsUpdated }) {
  const [selectedAssociations, setSelectedAssociations] = useState([]);
  const selectedAssociationsRef = useRef([]);
  const applySelectedAssociations = (nextOrUpdater) => {
    setSelectedAssociations((prev) => {
      const next =
        typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
      selectedAssociationsRef.current = next;
      return next;
    });
  };
  const [suggestedEntities, setSuggestedEntities] = useState(new Map());
  const [entities, setEntities] = useState({
    days: [],
    accommodations: [],
    places: [],
    travel: []
  });
  const [loading, setLoading] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [showAutoScrollLoader, setShowAutoScrollLoader] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    days: false,
    accommodations: false,
    places: false,
    travel: false
  });

  const { currentUser } = useAuth();
  const { effectiveUserId } = useTrip();
  const { tripId } = useParams();

  /**
   * Converts document connections to association format
   */
  const convertConnectionsToAssociations = (connections) => {
    return connections.map(connection => ({
      entityType: toRegistryEntityType(connection.entityType),
      entityId: connection.entityId,
      entityName: connection.entityName,
      selectedAt: connection.associatedAt || new Date().toISOString(),
      isPreSelected: false, // Existing associations are not pre-selected
      connectionType: connection.connectionType || 'related'
    }));
  };

  /**
   * Fetches related entities for relationship-aware suggestions
   */
  const fetchRelatedEntities = async (associationsToCheck = null) => {
    const associations = associationsToCheck || selectedAssociations;

    if (!associations || associations.length === 0) {
      return [];
    }

    const relatedEntities = [];

    try {
      // For each existing association, find related entities
      for (const association of associations) {
        switch (toRegistryEntityType(association.entityType)) {
          case 'accommodation': {
            const accommodation = await accommodationService.getAccommodationById(
              effectiveUserId,
              tripId,
              association.entityId
            );

            if (accommodation && accommodation.contentBlocks) {
              accommodation.contentBlocks.forEach(block => {
                if (block.dayId) {
                  relatedEntities.push({
                    entityType: 'day',
                    entityId: block.dayId,
                    entityName: `Day ${block.date ? new Date(block.date).getDate() : ''}`,
                    isRelated: true,
                    relationshipType: 'accommodation-day-contentblock'
                  });
                }
              });

              if (accommodation.placeReference && accommodation.placeReference.placeId) {
                relatedEntities.push({
                  entityType: 'place',
                  entityId: accommodation.placeReference.placeId,
                  entityName: accommodation.placeReference.placeName || 'Related Place',
                  isRelated: true,
                  relationshipType: 'accommodation-place'
                });
              }
            }

            // Registry-aware (Issue 18): stays_on days + located_in place.
            try {
              const { models: dayModels } = await linkPresentationService.getRelatedEntityModels(
                effectiveUserId, tripId, 'accommodation', association.entityId, { surface: 'itinerary' }
              );
              (dayModels || [])
                .filter(m => m.entityType === 'day')
                .forEach(m => relatedEntities.push({
                  entityType: 'day',
                  entityId: m.entityId,
                  entityName: m.entityName,
                  isRelated: true,
                  relationshipType: 'accommodation-day-registry'
                }));

              const { models: placeModels } = await linkPresentationService.getRelatedEntityModels(
                effectiveUserId, tripId, 'accommodation', association.entityId, { surface: 'related' }
              );
              (placeModels || [])
                .filter(m => m.entityType === 'place')
                .forEach(m => relatedEntities.push({
                  entityType: 'place',
                  entityId: m.entityId,
                  entityName: m.entityName,
                  isRelated: true,
                  relationshipType: 'accommodation-place-registry'
                }));
            } catch (error) {
              console.warn('ManageAssociationsModal: could not read registry links for accommodation', error);
            }
            break;
          }

          case 'day': {
            const allAccommodations = await accommodationService.fetchAccommodation(
              effectiveUserId,
              tripId
            );

            allAccommodations.forEach(accommodation => {
              if (accommodation.contentBlocks) {
                const dayBlock = accommodation.contentBlocks.find(
                  block => block.dayId === association.entityId
                );
                if (dayBlock) {
                  relatedEntities.push({
                    entityType: 'accommodation',
                    entityId: accommodation.id,
                    entityName: accommodation.summary || accommodation.name || 'Accommodation',
                    isRelated: true,
                    relationshipType: 'day-accommodation-contentblock'
                  });

                  if (accommodation.placeReference && accommodation.placeReference.placeId) {
                    relatedEntities.push({
                      entityType: 'place',
                      entityId: accommodation.placeReference.placeId,
                      entityName: accommodation.placeReference.placeName || 'Related Place',
                      isRelated: true,
                      relationshipType: 'day-place-via-accommodation'
                    });
                  }
                }
              }
            });

            // Registry-aware (Issue 18): stays_on accommodations for this day.
            try {
              const { models } = await linkPresentationService.getRelatedEntityModels(
                effectiveUserId, tripId, 'day', association.entityId, { surface: 'itinerary' }
              );
              (models || [])
                .filter(m => m.entityType === 'accommodation')
                .forEach(m => relatedEntities.push({
                  entityType: 'accommodation',
                  entityId: m.entityId,
                  entityName: m.entityName,
                  isRelated: true,
                  relationshipType: 'day-accommodation-registry'
                }));
            } catch (error) {
              console.warn('ManageAssociationsModal: could not read registry links for day', error);
            }
            break;
          }

          case 'place': {
            // accommodationService.getAccommodationsByPlace is registry-aware (Issue 6)
            const linkedAccommodations = await accommodationService.getAccommodationsByPlace(
              effectiveUserId,
              tripId,
              association.entityId
            );

            for (const accommodation of linkedAccommodations) {
              relatedEntities.push({
                entityType: 'accommodation',
                entityId: accommodation.id,
                entityName: accommodation.summary || accommodation.name || 'Accommodation',
                isRelated: true,
                relationshipType: 'place-accommodation'
              });

              if (accommodation.contentBlocks) {
                accommodation.contentBlocks.forEach(block => {
                  if (block.dayId) {
                    relatedEntities.push({
                      entityType: 'day',
                      entityId: block.dayId,
                      entityName: `Day ${block.date ? new Date(block.date).getDate() : ''}`,
                      isRelated: true,
                      relationshipType: 'place-day-via-accommodation'
                    });
                  }
                });
              }

              // Registry-aware (Issue 18): this hotel's stays_on days.
              try {
                const { models } = await linkPresentationService.getRelatedEntityModels(
                  effectiveUserId, tripId, 'accommodation', accommodation.id, { surface: 'itinerary' }
                );
                (models || [])
                  .filter(m => m.entityType === 'day')
                  .forEach(m => relatedEntities.push({
                    entityType: 'day',
                    entityId: m.entityId,
                    entityName: m.entityName,
                    isRelated: true,
                    relationshipType: 'place-day-via-accommodation-registry'
                  }));
              } catch (error) {
                console.warn('ManageAssociationsModal: could not read registry stays for accommodation', accommodation.id, error);
              }
            }
            break;
          }
        }
      }

      // Remove duplicates and existing associations
      const uniqueRelated = relatedEntities.filter((entity, index, arr) => {
        const isDuplicate = arr.findIndex(e =>
          e.entityType === entity.entityType && e.entityId === entity.entityId
        ) !== index;

        const isAlreadySelected = associations.some(selected =>
          selected.entityType === entity.entityType && selected.entityId === entity.entityId
        );

        return !isDuplicate && !isAlreadySelected;
      });

      return uniqueRelated;
    } catch (error) {
      console.error('Error fetching related entities:', error);
      return [];
    }
  };

  /**
   * Fetches all entities for the hierarchical association interface
   */
  const fetchEntities = async () => {
    if (!currentUser || !tripId) return;

    setLoading(true);
    try {
      const [days, accommodations, places, travel] = await Promise.all([
        daysService.fetchDays(effectiveUserId, tripId),
        accommodationService.fetchAccommodation(effectiveUserId, tripId),
        placesService.fetchPlaces(effectiveUserId, tripId),
        travelService.fetchTravel(effectiveUserId, tripId)
      ]);

      console.log('🔍 [ManageAssociationsModal] Fetched entities:', {
        days: (days || []).length,
        accommodations: (accommodations || []).length,
        places: (places || []).length,
        travel: (travel || []).length
      });
      console.log('🔍 [ManageAssociationsModal] Travel entities:', travel);

      setEntities({
        days: days || [],
        accommodations: accommodations || [],
        places: places || [],
        travel: travel || []
      });

      // Existing associations: legacy connections[] and/or registry document_for links
      const documentIdForRegistry = document.firestoreId || document.id;
      console.log('🔍 [ManageAssociationsModal] Document connections:', document.connections);
      let rawAssociations = convertConnectionsToAssociations(document.connections || []);

      const linkConfig = await linkRegistryConfigService.getLinkRegistryConfig(
        effectiveUserId,
        tripId
      );
      if (shouldReadFromRegistry(linkConfig) && documentIdForRegistry) {
        const { source, models } = await linkPresentationService.getRelatedEntityModels(
          effectiveUserId,
          tripId,
          'document',
          documentIdForRegistry,
          { surface: 'related' }
        );
        if (source === 'registry' && models?.length) {
          const fromRegistry = models
            .filter((model) => model.entityType !== 'document')
            .map((model) => ({
              entityType: toRegistryEntityType(model.entityType),
              entityId: model.entityId,
              entityName: model.entityName || 'Unnamed Entity',
              selectedAt: new Date().toISOString(),
              isPreSelected: true,
              connectionType: 'related'
            }));
          const seen = new Set();
          rawAssociations = [...fromRegistry, ...rawAssociations].filter((association) => {
            const key = `${toRegistryEntityType(association.entityType)}:${association.entityId}`;
            if (seen.has(key)) {
              return false;
            }
            seen.add(key);
            return true;
          });
        }
      }

      console.log('🔍 [ManageAssociationsModal] Raw associations:', rawAssociations);

      // Filter out stale connections (entities that no longer exist)
      const validAssociations = rawAssociations.filter(association => {
        const { entityType, entityId, entityName } = association;
        console.log(`🔍 [ManageAssociationsModal] Checking association: type=${entityType}, id=${entityId}, name=${entityName}`);

        const registryType = toRegistryEntityType(entityType);
        let isValid = false;
        switch (registryType) {
          case 'day':
            isValid = (days || []).some(day => day.id === entityId);
            break;
          case 'accommodation':
            isValid = (accommodations || []).some(acc => acc.id === entityId);
            break;
          case 'place':
            isValid = (places || []).some(place => place.id === entityId);
            break;
          case 'travel':
            isValid = (travel || []).some(t => t.id === entityId);
            break;
          default:
            console.warn(`❌ [ManageAssociationsModal] Unknown entity type in connection: ${entityType}`);
            isValid = false;
        }

        console.log(`🔍 [ManageAssociationsModal] Association ${entityType}/${entityId} valid: ${isValid}`);
        return isValid;
      });

      // Log if any stale connections were filtered out
      const staleCount = rawAssociations.length - validAssociations.length;
      console.log(`🔍 [ManageAssociationsModal] Association filtering: ${rawAssociations.length} raw → ${validAssociations.length} valid (${staleCount} filtered)`);
      console.log('🔍 [ManageAssociationsModal] Valid associations:', validAssociations);
      if (staleCount > 0) {
        console.log(`⚠️ Filtered out ${staleCount} stale connection(s) referencing non-existent entities`);
      }

      // Auto-expand sections that have existing VALID associations


      const sectionMap = {
        'day': 'days',
        'accommodation': 'accommodations',
        'place': 'places',
        'travel': 'travel'
      };

      const sectionsToExpand = {};
      validAssociations.forEach(association => {
        const section = sectionMap[toRegistryEntityType(association.entityType)];
        if (section) {
          sectionsToExpand[section] = true;
        }
      });

      setExpandedSections(prev => ({
        ...prev,
        ...sectionsToExpand
      }));

      // Add relationship-aware suggestions based on existing associations
      if (validAssociations.length > 0) {
        // Temporarily set selected associations so fetchRelatedEntities can work
        applySelectedAssociations(validAssociations);

        try {
          const relatedEntities = await fetchRelatedEntities(validAssociations);
          if (relatedEntities.length > 0) {
            // Don't auto-select suggested associations, just mark them for highlighting
            // Store suggestions separately for rendering logic
            const suggestionMap = new Map();
            relatedEntities.forEach(entity => {
              const key = `${entity.entityType}-${entity.entityId}`;
              suggestionMap.set(key, {
                isRelated: true,
                relationshipType: entity.relationshipType
              });
            });

            // Store suggestions for use in rendering
            setSuggestedEntities(suggestionMap);

            // Only set existing associations, not suggested ones
            console.log('🔍 [ManageAssociationsModal] Setting selectedAssociations:', validAssociations);
            applySelectedAssociations(validAssociations);

            // Expand sections for suggested associations
            relatedEntities.forEach(entity => {
              const section = sectionMap[toRegistryEntityType(entity.entityType)];
              if (section) {
                sectionsToExpand[section] = true;
              }
            });

            setExpandedSections(prev => ({
              ...prev,
              ...sectionsToExpand
            }));
          }
        } catch (relatedError) {
          console.error('Error fetching related entities:', relatedError);
          // Still show existing associations even if related entities fail
          applySelectedAssociations(validAssociations);
        }
      } else {
        // No existing associations, just set empty state
        applySelectedAssociations([]);
      }

    } catch (error) {
      console.error('Error fetching entities:', error);
      setUpdateError('Failed to load entities. Please try again.');
    } finally {
      setLoading(false);
    }

  };

  /**
   * Validates the selected associations
   */
  const validateAssociations = () => {
    const errors = [];

    selectedAssociations.forEach(association => {
      const { entityType, entityId, entityName } = association;
      const registryType = toRegistryEntityType(entityType);

      switch (registryType) {
        case 'day':
          if (!entities.days.find(day => day.id === entityId)) {
            errors.push(`Selected day "${entityName}" no longer exists`);
          }
          break;
        case 'accommodation':
          if (!entities.accommodations.find(acc => acc.id === entityId)) {
            errors.push(`Selected accommodation "${entityName}" no longer exists`);
          }
          break;
        case 'place':
          if (!entities.places.find(place => place.id === entityId)) {
            errors.push(`Selected place "${entityName}" no longer exists`);
          }
          break;
        case 'travel':
          if (!entities.travel.find(travel => travel.id === entityId)) {
            errors.push(`Selected travel "${entityName}" no longer exists`);
          }
          break;
        default:
          errors.push(`Unknown entity type: ${entityType}`);
      }
    });

    return errors;
  };

  /**
   * Handles saving the updated associations
   */
  const handleSaveAssociations = async () => {
    const errors = validateAssociations();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setLoading(true);
    setUpdateError('');
    setValidationErrors([]);

    try {
      // Use the correct document ID (firestoreId if available, otherwise id)
      const documentId = document.firestoreId || document.id;

      const associationsToSave =
        selectedAssociationsRef.current.length > 0
          ? [...selectedAssociationsRef.current]
          : [...selectedAssociations];

      const linkConfig = await linkRegistryConfigService.getLinkRegistryConfig(
        effectiveUserId,
        tripId
      );

      if (shouldReadFromRegistry(linkConfig)) {
        await documentService.syncRegistryDocumentAssociations(
          effectiveUserId,
          tripId,
          documentId,
          associationsToSave
        );
      } else {
        await documentService.clearConnections(effectiveUserId, tripId, documentId);
        for (const association of associationsToSave) {
          await documentService.addConnection(
            effectiveUserId,
            tripId,
            documentId,
            {
              entityType: association.entityType,
              entityId: association.entityId,
              entityName: association.entityName,
              connectionType: association.connectionType || 'related',
              associatedAt: new Date().toISOString()
            }
          );
        }
      }

      if (onAssociationsUpdated) {
        onAssociationsUpdated(associationsToSave);
      }
      onClose();
    } catch (error) {
      console.error('Error updating associations:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        documentId: document.firestoreId || document.id,
        tripId,
        selectedAssociationsCount: selectedAssociations.length
      });

      // Provide more specific error message
      let errorMessage = 'Failed to update associations. ';
      if (error.message.includes('permissions')) {
        errorMessage += 'Permission denied. Please check your access rights.';
      } else if (error.message.includes('not found')) {
        errorMessage += 'Document not found. Please refresh and try again.';
      } else {
        errorMessage += `Error: ${error.message}`;
      }

      setUpdateError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggles section expansion
   */
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  /**
   * Handles entity selection/deselection
   * Handles travel subtypes polymorphically
   */
  const toggleEntitySelection = (entityType, entityId, entityName) => {
    const registryType = toRegistryEntityType(entityType);

    applySelectedAssociations((prev) => {
      const existingIndex = prev.findIndex(item => {
        if (item.entityId !== entityId) return false;
        return associationEntityTypesMatch(item.entityType, registryType);
      });

      if (existingIndex >= 0) {
        return prev.filter((_, index) => index !== existingIndex);
      } else {
        return [...prev, {
          entityType: registryType,
          entityId,
          entityName,
          selectedAt: new Date().toISOString(),
          isPreSelected: false,
          connectionType: 'related'
        }];
      }
    });
  };

  /**
   * Checks if an entity is selected
   * Handles travel subtypes (flight, train, etc.) when entityType is 'travel'
   */
  /**
   * Checks if an entity is selected
   * Handles travel subtypes (flight, train, etc.) polymorphically
   */
  const isEntitySelected = (entityType, entityId) => {
    const registryType = toRegistryEntityType(entityType);

    return selectedAssociations.some(item => {
      if (item.entityId !== entityId) return false;
      return associationEntityTypesMatch(item.entityType, registryType);
    });
  };

  /**
   * Checks if an entity is suggested (but not necessarily selected)
   */
  const isEntitySuggested = (entityType, entityId) => {
    const key = `${entityType}-${entityId}`;
    return suggestedEntities.has(key);
  };

  /**
   * Gets the suggestion type for an entity
   */
  const getSuggestionType = (entityType, entityId) => {
    const key = `${entityType}-${entityId}`;
    const suggestion = suggestedEntities.get(key);
    if (!suggestion) return null;

    return suggestion.isRelated ? 'related' : 'direct';
  };

  /**
   * Formats date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  /**
   * Renders entity items for a section
   */
  const renderEntityItems = (entityType, entities, getDisplayName, getIcon) => {
    if (!entities || entities.length === 0) {
      return (
        <div className="entity-empty">
          <span>No {entityType} found</span>
        </div>
      );
    }

    return entities.map((entity, index) => {
      const displayName = getDisplayName(entity, index);
      const specificType = entity.type || entityType;
      const linkType = toRegistryEntityType(specificType);
      const isSelected = isEntitySelected(linkType, entity.id);
      const isSuggested = isEntitySuggested(linkType, entity.id);
      const suggestionType = getSuggestionType(linkType, entity.id);

      let suggestionClass = '';
      let suggestionLabel = '';
      let tooltipText = '';

      if (isSuggested && suggestionType === 'related') {
        suggestionClass = 'suggested-related';
        suggestionLabel = ' (Suggested)';
        tooltipText = 'Suggested due to existing association';
      }

      return (
        <div
          key={entity.id}
          className={`entity-item ${isSelected ? 'selected' : ''} ${suggestionClass}`}
          data-entity-type={linkType}
          data-entity-id={entity.id}
          onClick={() => toggleEntitySelection(linkType, entity.id, displayName)}
          title={tooltipText || undefined}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => { }} // Handled by parent div click
            className="entity-checkbox"
          />
          <span className="entity-icon">{getIcon(entity)}</span>
          <span className="entity-name">
            {displayName}
            {suggestionLabel && <span className="suggested-indicator">{suggestionLabel}</span>}
          </span>
        </div>
      );
    });
  };

  // Fetch entities when component mounts
  useEffect(() => {
    fetchEntities();
  }, [currentUser, tripId, document]);

  if (!document) {
    return null;
  }

  return (
    <div className="upload-modal-overlay">
      <div
        className="upload-modal-dialog upload-modal-container"
        role="dialog"
        aria-modal="true"
        aria-label="Manage Document Associations"
      >
        <button className="upload-modal-close" onClick={onClose} title="Close" aria-label="Close">&times;</button>

        <div className="upload-modal-header">
          <h2>Manage Document Associations</h2>
          <p>Associate "{document.title || document.name}" with trip items</p>
        </div>

        <div className="association-container">
          <div className="association-content">
            {updateError && (
              <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>
                {updateError}
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="validation-errors" style={{ marginBottom: '1rem' }}>
                <div style={{ color: '#d32f2f', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Please fix the following issues:
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#d32f2f' }}>
                  {validationErrors.map((error, index) => (
                    <li key={index} style={{ marginBottom: '0.25rem' }}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="association-legend">
              <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '0.9rem' }}>
                Select items to associate with this document:
              </p>
              <div className="legend-items">
                <div className="legend-item">
                  <span className="legend-color blue"></span>
                  <span>Blue: Currently associated</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color green"></span>
                  <span>Green: Suggested based on existing relationships</span>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="loading">
                <div className="spinner"></div>
                Please wait while the document is being analysed
              </div>
            ) : (
              <div className="association-wrapper">
                <div className="hierarchical-association">
                  {/* Days Section */}
                  <div className="association-section">
                    <div
                      className="section-header"
                      onClick={() => toggleSection('days')}
                    >
                      <span className="expand-icon">
                        {expandedSections.days ? <FaChevronDown /> : <FaChevronRight />}
                      </span>
                      <FaCalendarAlt className="section-icon" />
                      <span className="section-title">Days ({entities.days.length})</span>
                    </div>

                    {expandedSections.days && (
                      <div className="section-content">
                        {renderEntityItems(
                          'day',
                          entities.days,
                          (day, index) => `Day ${index + 1} - ${formatDate(day.date)}`,
                          () => <FaCalendarAlt />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Accommodations Section */}
                  <div className="association-section">
                    <div
                      className="section-header"
                      onClick={() => toggleSection('accommodations')}
                    >
                      <span className="expand-icon">
                        {expandedSections.accommodations ? <FaChevronDown /> : <FaChevronRight />}
                      </span>
                      <FaHotel className="section-icon" />
                      <span className="section-title">Accommodations ({entities.accommodations.length})</span>
                    </div>

                    {expandedSections.accommodations && (
                      <div className="section-content">
                        {renderEntityItems(
                          'accommodation',
                          entities.accommodations,
                          (acc, index) => `${acc.summary || acc.details?.name} - ${acc.details?.city || ''}`,
                          () => <FaHotel />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Places Section */}
                  <div className="association-section">
                    <div
                      className="section-header"
                      onClick={() => toggleSection('places')}
                    >
                      <span className="expand-icon">
                        {expandedSections.places ? <FaChevronDown /> : <FaChevronRight />}
                      </span>
                      <FaMapMarkerAlt className="section-icon" />
                      <span className="section-title">Places ({entities.places.length})</span>
                    </div>

                    {expandedSections.places && (
                      <div className="section-content">
                        {renderEntityItems(
                          'place',
                          entities.places,
                          (place, index) => place.name || 'Unnamed Place',
                          () => <FaMapMarkerAlt />
                        )}
                      </div>
                    )}
                  </div>

                  <div className="association-section">
                    <div
                      className="section-header"
                      onClick={() => toggleSection('travel')}
                    >
                      <span className="expand-icon">
                        {expandedSections.travel ? <FaChevronDown /> : <FaChevronRight />}
                      </span>
                      <FaPlane className="section-icon" />
                      <span className="section-title">Travel ({entities.travel.length})</span>
                    </div>

                    {expandedSections.travel && (
                      <div className="section-content">
                        {renderEntityItems(
                          'travel',
                          entities.travel,
                          (travel, index) => `${travel.summary || travel.details?.type} - ${travel.details?.from || ''} to ${travel.details?.to || ''}`,
                          () => <FaPlane />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="association-actions">
            <button
              className="btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={() => {
                flushSync(() => {});
                void handleSaveAssociations();
              }}
              disabled={loading || validationErrors.length > 0}
              title={validationErrors.length > 0 ? 'Please fix validation errors before saving' : undefined}
            >
              {loading ? 'Saving...' : 'Save Associations'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManageAssociationsModal;